import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import { errorMessage } from '../api/client';
import type { BrokerContract } from '../api/types';
import { fmtDate } from '../ops/format';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { contractModelDescriptions, contractModelLabels } from './format';

type Model = 'OWN' | 'ORDER_PRICE';

function ContractLine({ c }: { c: BrokerContract }) {
  return (
    <>
      <span className={c.model === 'ORDER_PRICE' ? 'badge badge-remote' : 'badge'}>{contractModelLabels[c.model]}</span>
      {c.is_test && <span className="badge badge-warn">Testkonto</span>} seit {fmtDate(c.started_at)}
      {c.ended_at && ` bis ${fmtDate(c.ended_at)}`}
      {c.note && <span className="muted"> · {c.note}</span>}
    </>
  );
}

// BrokerContractSection: Vertragsmodell eines Maklers (OFFENE_FRAGEN B-22) – eigener Makler (Maklergeschäft bei
// Maklerplan) oder Partner im Auftragspreis, optional als Testkonto. Ändern beendet das bisherige Modell (Verlauf).
export function BrokerContractSection({ userId, name }: { userId: string; name: string }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const qc = useQueryClient();
  const base = `/p/${project.key}`;
  const q = useQuery({ queryKey: projectKeys.brokerContract(project.id, userId), queryFn: ({ signal }) => papi.brokerContract(userId, signal) });
  const [form, setForm] = useState<{ model: Model; isTest: boolean; note: string } | null>(null);
  const save = useMutation({
    mutationFn: (f: { model: Model; isTest: boolean; note: string }) => papi.setBrokerContract(userId, { model: f.model, is_test: f.isTest, note: f.note }),
    onSuccess: (data) => {
      qc.setQueryData(projectKeys.brokerContract(project.id, userId), data);
      void qc.invalidateQueries({ queryKey: projectKeys.team(project.id) });
      void qc.invalidateQueries({ queryKey: ['projects', project.id, 'price-list'] });
      setForm(null);
    },
  });
  const current = q.data?.current ?? null;
  const earlier = (q.data?.history ?? []).filter((c) => c.ended_at);

  return (
    <section className="card" aria-labelledby="contract-title">
      <h2 id="contract-title">Vertragsmodell</h2>
      {q.isPending ? (
        <p className="muted">Wird geladen …</p>
      ) : q.isError ? (
        <p className="confirmation-failed">{errorMessage(q.error)}</p>
      ) : current ? (
        <>
          <p>
            <ContractLine c={current} />
          </p>
          <p className="muted">{contractModelDescriptions[current.model]}</p>
          {current.model === 'ORDER_PRICE' && <Link to={`${base}/preisliste?makler=${userId}`}>Preisliste von {name} ansehen</Link>}
        </>
      ) : (
        <p className="muted">Noch nicht festgelegt.</p>
      )}
      {can('project.manage') &&
        (form ? (
          <form
            className="contract-form"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(form);
            }}
          >
            <fieldset>
              <legend>Modell</legend>
              {(['OWN', 'ORDER_PRICE'] as const).map((m) => (
                <label key={m} className="checkbox">
                  <input type="radio" name="contract-model" checked={form.model === m} onChange={() => setForm({ ...form, model: m })} />
                  <span>
                    <strong>{contractModelLabels[m]}</strong> – {contractModelDescriptions[m]}
                  </span>
                </label>
              ))}
            </fieldset>
            <label className="checkbox">
              <input type="checkbox" checked={form.isTest} onChange={(e) => setForm({ ...form, isTest: e.target.checked })} /> Testkonto (kein echter
              Partner, z. B. Mitarbeiter zum Ausprobieren)
            </label>
            <label className="field">
              <span>Notiz</span>
              <input value={form.note} maxLength={500} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </label>
            {save.isError && <p className="confirmation-failed">{errorMessage(save.error)}</p>}
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={save.isPending}>
                Speichern
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setForm(null)}>
                Abbrechen
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            className="btn btn-small"
            onClick={() => setForm({ model: (current?.model as Model | undefined) ?? 'OWN', isTest: current?.is_test ?? false, note: current?.note ?? '' })}
          >
            {current ? 'Modell ändern' : 'Modell festlegen'}
          </button>
        ))}
      {earlier.length > 0 && (
        <details className="contract-history">
          <summary>Verlauf ({earlier.length})</summary>
          <ul>
            {earlier.map((c) => (
              <li key={c.id}>
                <ContractLine c={c} />
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
